import { useCallback, useEffect, useRef, useState } from "react";

import { TV_CHART_BRIDGE_ACTION } from "../lib/TvChartBridge/actions";
import createChartActionsBridge from "../lib/TvChartBridge/createChartActionsBridge";
import createShellDataFeedBridge from "../lib/TvChartBridge/createShellDataFeedBridge";

const defaultPostMessage = (target, message) => {
  target.postMessage(message);
};

const defaultAddMessageListener = listener => {
  window.addEventListener("message", listener);

  return () => {
    window.removeEventListener("message", listener);
  };
};

/**
 * Web iframe messaging for TvChartBridge.
 * Shell owns dataFeed + chart actions + save handler; HTML owns widget + proxy datafeed.
 */
export default function useTvChartBridgeFrameMessaging({
  initPayload,
  dataFeed,
  chartActionHandlers,
  onSaveChart,
  onMarkClick,
  onOpenPriceAlert,
  onOpenStocksList,
  onOpenFullscreenChart,
  backups,
  markersPayload,
  drawnObjectsOptions,
  priceAlerts,
  stockCurrency,
  postMessage = defaultPostMessage,
  addMessageListener = defaultAddMessageListener,
  getMessageTarget = frameRef => frameRef.current?.contentWindow
}) {
  const frameRef = useRef(null);
  const [frameLoaded, setFrameLoaded] = useState(false);
  const [initialized, setInitialized] = useState(false);
  const [chartReady, setChartReady] = useState(false);
  const dataFeedBridgeRef = useRef(null);
  const chartActionsBridgeRef = useRef(null);
  const onSaveChartRef = useRef(onSaveChart);
  const onMarkClickRef = useRef(onMarkClick);
  const onOpenPriceAlertRef = useRef(onOpenPriceAlert);
  const onOpenStocksListRef = useRef(onOpenStocksList);
  const onOpenFullscreenChartRef = useRef(onOpenFullscreenChart);

  onSaveChartRef.current = onSaveChart;
  onMarkClickRef.current = onMarkClick;
  onOpenPriceAlertRef.current = onOpenPriceAlert;
  onOpenStocksListRef.current = onOpenStocksList;
  onOpenFullscreenChartRef.current = onOpenFullscreenChart;

  const sendToFrame = useCallback(
    payload => {
      const target = getMessageTarget(frameRef);

      if (target) {
        postMessage(target, JSON.stringify(payload));
      }
    },
    [getMessageTarget, postMessage]
  );

  useEffect(() => {
    if (!dataFeed) {
      dataFeedBridgeRef.current = null;
      return;
    }

    dataFeedBridgeRef.current = createShellDataFeedBridge({
      dataFeed,
      postMessage: message => {
        const target = getMessageTarget(frameRef);

        if (target) {
          postMessage(target, message);
        }
      }
    });
  }, [dataFeed, getMessageTarget, postMessage]);

  useEffect(() => {
    if (!chartActionHandlers) {
      chartActionsBridgeRef.current = null;
      return;
    }

    chartActionsBridgeRef.current = createChartActionsBridge({
      ...chartActionHandlers,
      postMessage: message => {
        const target = getMessageTarget(frameRef);

        if (target) {
          postMessage(target, message);
        }
      }
    });
  }, [chartActionHandlers, getMessageTarget, postMessage]);

  const onFrameLoad = useCallback(() => {
    setFrameLoaded(true);
  }, []);

  useEffect(() => {
    const handleMessage = event => {
      const target = getMessageTarget(frameRef);

      if (target && event.source && event.source !== target) {
        return;
      }

      let parsed;

      try {
        parsed = JSON.parse(event.data);
      } catch (error) {
        return;
      }

      if (parsed.action === TV_CHART_BRIDGE_ACTION.WEBVIEW_LOADED) {
        setFrameLoaded(true);
        return;
      }

      if (parsed.action === TV_CHART_BRIDGE_ACTION.SAVE_CHART) {
        onSaveChartRef.current?.(parsed.data || {});
        return;
      }

      if (parsed.action === TV_CHART_BRIDGE_ACTION.CHART_READY) {
        setChartReady(true);
        return;
      }

      if (parsed.action === TV_CHART_BRIDGE_ACTION.MARK_CLICK) {
        onMarkClickRef.current?.(parsed.data || {});
        return;
      }

      if (parsed.action === TV_CHART_BRIDGE_ACTION.OPEN_PRICE_ALERT) {
        onOpenPriceAlertRef.current?.(parsed.data || {});
        return;
      }

      if (parsed.action === TV_CHART_BRIDGE_ACTION.OPEN_STOCKS_LIST) {
        onOpenStocksListRef.current?.(parsed.data || {});
        return;
      }

      if (parsed.action === TV_CHART_BRIDGE_ACTION.OPEN_FULLSCREEN_CHART) {
        onOpenFullscreenChartRef.current?.(parsed.data || {});
        return;
      }

      if (chartActionsBridgeRef.current?.handleMessage(parsed)) {
        return;
      }

      dataFeedBridgeRef.current?.handleMessage(parsed);
    };

    return addMessageListener(handleMessage);
  }, [addMessageListener, getMessageTarget]);

  // New bootstrap (e.g. stock switch) rebuilds initPayload while this hook may
  // stay mounted. The iframe is remounted, so wait for a fresh load before INIT;
  // a second init on an already-created widget is ignored in tvchartbridge.html.
  useEffect(() => {
    setInitialized(false);
    setChartReady(false);
    setFrameLoaded(false);
  }, [initPayload]);

  useEffect(() => {
    if (!frameLoaded || !initPayload || !dataFeed || initialized) {
      return;
    }

    sendToFrame({
      action: TV_CHART_BRIDGE_ACTION.INIT,
      data: initPayload
    });
    setInitialized(true);
  }, [dataFeed, frameLoaded, initPayload, initialized, sendToFrame]);

  useEffect(() => {
    if (!initialized || !chartActionsBridgeRef.current) {
      return;
    }

    chartActionsBridgeRef.current.pushBackupsUpdated(backups || []);
  }, [backups, initialized]);

  useEffect(() => {
    if (!chartReady || !chartActionsBridgeRef.current) {
      return;
    }

    chartActionsBridgeRef.current.pushMarkersUpdate(markersPayload || {});
    chartActionsBridgeRef.current.pushDrawnObjectsOptionsUpdate(
      drawnObjectsOptions || []
    );
    chartActionsBridgeRef.current.pushPriceAlertsUpdate({
      priceAlerts: priceAlerts || [],
      currency: stockCurrency
    });
    dataFeedBridgeRef.current?.pushShapesNow?.();
  }, [
    chartReady,
    markersPayload,
    drawnObjectsOptions,
    priceAlerts,
    stockCurrency
  ]);

  return {
    frameRef,
    isFrameReady: frameLoaded && initialized,
    onFrameLoad
  };
}
